import { useMemo } from 'react';
import { List } from '@mui/material';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { LegStatus } from '@api/Trinium/types';
import Tag from '@components/Tag';
import AvailableNowAction from '@components/DataGrid/Dispatch/Actions/AvailableNow';
import DeliverAction from '@components/DataGrid/Dispatch/Actions/DeliverAction';
import PickupAction from '@components/DataGrid/Dispatch/Actions/PickupAction';
import PickupAndDeliverAction from '@components/DataGrid/Dispatch/Actions/PickupAndDeliverAction';
import UndoStatusAction from '@components/DataGrid/Dispatch/Actions/UndoStatusAction';
import { queueModal } from '@components/Modals/modalSlice';
import ButtonDropdown, { PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import { useAppDispatch } from '@reduxStore';

import tagStyles from '@components/Tag.module.css';

interface LegStatusSelectorProps {
	leg: DispatchLeg;
	nextLegStatus?: keyof typeof LegStatus;
	width?: string | number;
	height?: string | number;
	isDisabled?: boolean;
	onError?: (error: string) => void;
	/** Grid rows currently selected, resolved lazily so the cell does not subscribe to selection state */
	getSelectedRows?: () => DispatchLeg[];
}

/**
 * Component for rendering the Leg Status Button Dropdown
 */
export default function LegStatusSelector({ leg, nextLegStatus, width = 125, height = 18, onError, isDisabled, getSelectedRows }: LegStatusSelectorProps) {
	const status = getEnumValueByKey(LegStatus, leg.legStatus);

	switch (status) {
		case LegStatus.AC:
		case LegStatus.AV:
		case LegStatus.NA:
		case LegStatus.OS:
		case LegStatus.DL:
		case LegStatus.PU: {
			return (
				<ButtonDropdown
					buttonConfig={{
						width: width,
						height: height,
						className: `
							${tagStyles.tag}
							${tagStyles['tag-btn']}
							${tagStyles[leg.legStatus]}
						`
					}}
					ButtonContent={() => status}
					buttonContentProps={{ status }}
					PopupContent={LegStatusList}
					popupContentProps={{
						leg,
						nextLegStatus,
						onError,
						getSelectedRows
					}}
					isDisabled={isDisabled}
				/>
			);
		}
		default: {
			return (
				<Tag
					text={status}
					tagStyle={leg.legStatus}
					width={width}
					height={height}
					align='left'
				/>
			);
		}
	}
}

function LegStatusList({
	onChange,
	onError,
	leg,
	nextLegStatus,
	getSelectedRows
}: PopupContentProps & {
	leg: DispatchLeg;
	nextLegStatus?: LegStatus;
	onError: (error: string) => void;
	getSelectedRows?: () => DispatchLeg[];
}) {
	const appDispatch = useAppDispatch();
	const status = getEnumValueByKey(LegStatus, leg.legStatus) as LegStatus;
	const selectedRows = useMemo(() => getSelectedRows?.(), [getSelectedRows]);
	const actionProps = {
		leg,
		nextLegStatus: nextLegStatus,
		onError,
		selectedRows
	};

	return (
		<List
			disablePadding
			className={tagStyles.actions}
			component='div'
			onClick={(event) => {
				onChange(event);

				/** Show Reminder Modal if Leg's isReminder is true */
				if (leg.isReminder) {
					appDispatch(
						queueModal({
							Component: 'reminderModal',
							Props: {
								legId: leg.legId
							}
						})
					);
				}
			}}
		>
			{[LegStatus.AV, LegStatus.AC].includes(status) && (
				<>
					<PickupAction {...actionProps} />
					<PickupAndDeliverAction {...actionProps} />
				</>
			)}
			{status === LegStatus.PU && <DeliverAction {...actionProps} />}
			{[LegStatus.NA, LegStatus.OS].includes(status) && <AvailableNowAction {...actionProps} />}
			<UndoStatusAction {...actionProps} />
		</List>
	);
}
